import { useEffect } from 'react'
import { useBranding } from '@/lib/useBranding'

// Change this date whenever the policy's content changes.
const LAST_UPDATED = '27 October 2026'

/**
 * The privacy policy, at /privacy — public, no sign-in, as Meta requires for
 * a WhatsApp Business app. The business's name and contact details come from
 * Settings > Company through the public branding API the page already reads
 * for its logo, so the page stays right when they change.
 *
 * What it says follows what this system actually does with a customer's
 * information. Change the two together.
 */
export default function PrivacyPolicy() {
  const { siteLogo, data } = useBranding()
  const company = data?.company

  useEffect(() => {
    document.title = 'Privacy Policy'
  }, [])

  const name = company?.name || 'the business'
  // Each on its own line, labelled, and a link that opens the right app.
  const digits = (number) => String(number).replace(/[^\d+]/g, '')
  // wa.me needs the full international number: a 10-digit Indian number is
  // given its 91, as the shop types it without one.
  const international = (number) => {
    const plain = digits(number).replace('+', '')
    return plain.length === 10 ? `91${plain}` : plain
  }
  const contact = [
    company?.email && { label: 'Email', text: company.email, href: `mailto:${company.email}` },
    company?.phone && { label: 'Phone', text: company.phone, href: `tel:${digits(company.phone)}` },
    company?.whatsapp && {
      label: 'WhatsApp',
      text: company.whatsapp,
      href: `https://wa.me/${international(company.whatsapp)}`,
    },
  ].filter(Boolean)

  const contactLines = contact.length > 0 && (
    <p className="legal-contact">
      {contact.map((c) => (
        <span key={c.label}>
          {c.label}:{' '}
          <a href={c.href} target={c.label === 'WhatsApp' ? '_blank' : undefined} rel="noreferrer">
            {c.text}
          </a>
        </span>
      ))}
    </p>
  )

  return (
    <div className="legal-page">
      <article className="legal-card">
        {siteLogo ? (
          <img src={siteLogo} alt="" className="legal-logo" />
        ) : (
          <span className="legal-logo logo-placeholder" aria-hidden="true" />
        )}
        <h1>Privacy Policy</h1>
        <p className="legal-updated">Last updated: {LAST_UPDATED}</p>

        <p>
          This policy explains what information {name} collects when you order from us on
          WhatsApp or through the order pages we send you, how we use it, who we share it with,
          and the choices you have.
        </p>

        <h2>1. Who we are</h2>
        <p>
          {company?.name ? <strong>{company.name}</strong> : name}
          {company?.address && (
            <>
              <br />
              <span className="pre-line">{company.address}</span>
            </>
          )}
        </p>
        {contactLines}

        <h2>2. Information we collect</h2>
        <ul>
          <li>
            <strong>Your WhatsApp details:</strong> your phone number, the name on your WhatsApp
            profile, and the language you choose.
          </li>
          <li>
            <strong>Your messages:</strong> the text, voice notes and photos you send to our
            WhatsApp number, and the buttons you tap.
          </li>
          <li>
            <strong>Order details:</strong> the products, quantities and prices on your orders;
            your business name and GSTIN if you give them; billing and delivery addresses,
            contact person and phone number; preferred delivery or pickup date; and the
            transport service you choose.
          </li>
          <li>
            <strong>Delivery information:</strong> dispatch and transport (LR) details, whether
            you confirmed the delivery, and anything you report as missing.
          </li>
          <li>
            <strong>Technical information:</strong> when you open an order page, our server
            records your IP address and browser type, as any website does. Order pages store a
            few settings (such as our logo) in your browser so they load quickly. We do not
            use advertising or tracking cookies.
          </li>
        </ul>

        <h2>3. How we use your information</h2>
        <ul>
          <li>To read your order — including transcribing voice notes and reading photos of
            written orders — and to price it.</li>
          <li>To send you your order page, confirmation codes, dispatch details, delivery
            dates, delivery reminders and other updates about your orders on WhatsApp.</li>
          <li>To pack, dispatch and deliver your orders, and to prepare invoices.</li>
          <li>To answer your questions and resolve problems with deliveries.</li>
          <li>To keep the records that tax and accounting laws require.</li>
          <li>To protect our service from misuse.</li>
        </ul>
        <p>
          We do not sell your information, and we do not use it for advertising. We send
          WhatsApp messages about your orders; we do not send promotional messages unless you
          have agreed to receive them.
        </p>

        <h2>4. Who we share it with</h2>
        <p>Only what each one needs to do its part:</p>
        <ul>
          <li>
            <strong>Meta (WhatsApp Business Platform)</strong> — carries the messages between
            you and us. WhatsApp&apos;s own privacy policy applies to your use of WhatsApp.
          </li>
          <li>
            <strong>AI service providers</strong> (such as Sarvam AI, and OpenRouter and the AI
            models it connects to) — receive your voice notes, photos and message text only to
            turn them into an order.
          </li>
          <li>
            <strong>Transport companies</strong> — receive the name, delivery address and phone
            number needed to deliver your order.
          </li>
          <li>
            <strong>Hosting and email providers</strong> — store our data and send our
            internal emails, on our behalf.
          </li>
          <li>
            <strong>Google reCAPTCHA</strong> — protects our staff sign-in page only; it is not
            used on your order pages.
          </li>
          <li>
            <strong>Authorities</strong> — when the law requires it, for example tax records.
          </li>
        </ul>

        <h2>5. How long we keep it</h2>
        <ul>
          <li>Order, invoice and payment records are kept for as long as tax and accounting
            laws require.</li>
          <li>Your messages, voice notes and photos are kept with the order they created, so we
            can check what was ordered if there is a question about it.</li>
          <li>Links to your order pages stop working 48 hours after they are sent.</li>
        </ul>

        <h2>6. How we protect it</h2>
        <p>
          Your information is stored on secured servers. Only our staff, signed in with their
          own accounts and limited to what their role needs, can see it. Passwords and keys are
          stored securely, and connections to our service are encrypted.
        </p>

        <h2>7. Your choices and rights</h2>
        <p>You can ask us to:</p>
        <ul>
          <li>tell you what information we hold about you;</li>
          <li>correct information that is wrong or incomplete;</li>
          <li>delete your information, except what we must keep by law (such as invoices);</li>
          <li>stop sending you WhatsApp messages.</li>
        </ul>
        <p>
          Send your request to us on WhatsApp or by email using the contact details above. We
          will reply within a reasonable time, and within the period the law sets. If you are
          not satisfied with our reply, you may complain to the Data Protection Board of India.
        </p>

        <h2>8. Children</h2>
        <p>Our service is meant for businesses and adults. We do not knowingly collect
          information from children.</p>

        <h2>9. Changes to this policy</h2>
        <p>
          If we change this policy, we will update it on this page and change the date at the
          top.
        </p>

        <h2>10. Contact Us</h2>
        <p>
          If you have questions about this Privacy Policy or your information, please contact{' '}
          {name}
          {contact.length > 0 ? ':' : ' using the WhatsApp number you order on.'}
        </p>
        {contactLines}
      </article>
    </div>
  )
}
